import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { labFlag, type LabInterpretation } from '@wodi/domain';
import type { PatientDetail, ReleasedLabReportView } from '@wodi/shared';
import { Button, LoadingBlock, Notice } from '@wodi/ui';
import { useId, useState } from 'react';

import { labSettingsQuery, patientLabResultsQuery } from '../api/lab.ts';
import { can, sessionQuery } from '../api/session.ts';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import styles from './lab.module.css';
import { CultureSummary, PreliminaryReport } from './culture-panel.tsx';
import { PrintReportDialog } from './print-report-dialog.tsx';
import { ResultTable } from './result-table.tsx';

/** Critical first, then abnormal, then the rest in report order (plan §8.7). */
function severity(i: LabInterpretation): number {
  const flag = labFlag(i);
  if (flag.critical) return 0;
  return flag.emphasised ? 1 : 2;
}

export function abnormalFirst(report: ReleasedLabReportView): ReleasedLabReportView {
  const results = report.results
    .map((r, index) => ({ r, index }))
    .sort(
      (a, b) => severity(a.r.interpretation) - severity(b.r.interpretation) || a.index - b.index,
    )
    .map(({ r }) => r);
  return { ...report, results };
}

/**
 * Released lab reports on the patient page (plan §8.7): newest first, the only results a
 * clinician sees before the consultation workspace exists. Results in progress never show.
 */
export function LabResultsSection({ patient }: { patient: PatientDetail }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const results = useQuery(patientLabResultsQuery(patient.id));
  const settings = useQuery({
    ...labSettingsQuery,
    enabled: session !== null && can(session, 'lab.read'),
  });
  const [printing, setPrinting] = useState<ReleasedLabReportView | null>(null);
  const id = useId();
  const canPrint = session !== null && can(session, 'lab.report.print');
  return (
    <section className={styles.section} aria-labelledby={id}>
      <h2 id={id} className={styles.sectionHeading}>
        Lab results
      </h2>
      {results.isPending ? <LoadingBlock label="Loading lab results" rows={3} /> : null}
      {results.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void results.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(results.error, "Lab results couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {results.isSuccess && results.data.items.length === 0 ? (
        <p>No released lab results.</p>
      ) : null}
      {(results.data?.preliminary ?? []).map((p) => (
        <div key={p.id} className={styles.stack}>
          <div className={styles.actions}>
            <strong>{p.testName}</strong>
            <span
              className={styles.meta}
            >{`Sample ${p.sampleNumber}. The final report follows.`}</span>
          </div>
          <PreliminaryReport report={p} testName={p.testName} />
        </div>
      ))}
      {(results.data?.items ?? []).map((report) => (
        <div key={report.id} className={styles.stack}>
          <div className={styles.actions}>
            <strong>{report.testName}</strong>
            <span className={styles.meta}>
              {`Sample ${report.sampleNumber}, released ${report.released === null ? '' : formatInstant(report.released.at)}`}
              {report.version > 1 ? `. Corrected: ${report.correctionReason ?? ''}` : ''}
            </span>
            {canPrint ? (
              <Button
                dense
                aria-label={`Print ${report.testName}`}
                onPress={() => {
                  setPrinting(report);
                }}
              >
                Print
              </Button>
            ) : null}
          </div>
          {report.results.length === 0 ? null : (
            <ResultTable
              report={abnormalFirst(report)}
              label={`${report.testName}, ${report.released === null ? '' : formatInstant(report.released.at)}`}
            />
          )}
          {report.culture === null ? null : (
            <CultureSummary culture={report.culture} label={report.testName} />
          )}
          {report.comment === null ? null : <p>{report.comment}</p>}
        </div>
      ))}
      {printing === null || session === null ? null : (
        <PrintReportDialog
          data={{
            facilityName: session.facility.name,
            paper: settings.data?.reportPaper ?? 'a4',
            patient: {
              displayName: patient.displayName,
              patientNumber: patient.patientNumber,
              sex: patient.sex.display,
              age: patient.age,
            },
            sampleNumber: printing.sampleNumber,
            specimen: printing.specimenType,
            collectedAt: printing.collectedAt,
            receivedAt: printing.receivedAt,
            requester: printing.requester,
            report: printing,
          }}
          onClose={() => {
            setPrinting(null);
          }}
        />
      )}
    </section>
  );
}
